import React, { useState } from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CloseIcon from '@material-ui/icons/Close';
import FilterListIcon from '@material-ui/icons/FilterList';
import Autocomplete from '@material-ui/lab/Autocomplete';

// Mesmo visual da linha "Filtros ativos" e do drawer das listas do sistema.
const useStyles = makeStyles((theme) => ({
  faixa: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    padding: '12px 16px',
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
  },
  grupo: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  rotulo: {
    color: theme.palette.text.secondary,
    marginRight: 4,
    whiteSpace: 'nowrap',
    fontWeight: 700,
  },
  botao: { borderRadius: 8, whiteSpace: 'nowrap' },
  chip: {
    borderRadius: 8,
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    color: '#1e40af',
    fontWeight: 600,
    fontSize: '0.75rem',
    '& .MuiChip-deleteIcon': {
      color: '#60a5fa',
      '&:hover': { color: '#1e40af' },
    },
  },
  drawer: {
    width: 360,
    height: '100%',
    boxSizing: 'border-box',
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    [theme.breakpoints.down('xs')]: { width: '85vw' },
  },
  drawerTitulo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  drawerAcoes: {
    display: 'flex',
    gap: 8,
    marginTop: 'auto',
    paddingTop: 16,
    borderTop: '1px solid #e2e8f0',
  },
  botaoLimpar: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    padding: '8px 16px',
    borderColor: '#e2e8f0',
    color: theme.palette.text.secondary,
  },
  botaoAplicar: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    padding: '8px 16px',
    backgroundColor: '#2563eb',
    color: '#fff',
    '&:hover': { backgroundColor: '#1d4ed8' },
  },
}));

const vazio = (valor) => valor === '' || valor === null || valor === undefined;

// Campo de busca com opções vindas de `campo.buscar(texto)` (valor = o objeto escolhido).
const CampoBusca = ({ campo, valor, onChange }) => {
  const [opcoes, setOpcoes] = useState([]);
  const rotuloOpcao = campo.rotuloOpcao || 'nome';

  async function buscar(texto) {
    try {
      setOpcoes(await campo.buscar(texto));
    } catch (e) {
      setOpcoes([]);
    }
  }

  // Carrega as primeiras opções ao abrir a lista.
  const todas =
    valor && !opcoes.some((opcao) => opcao.id === valor.id)
      ? [valor, ...opcoes]
      : opcoes;

  return (
    <Autocomplete
      size="small"
      fullWidth
      options={todas}
      value={valor || null}
      getOptionLabel={(opcao) => opcao[rotuloOpcao] || ''}
      getOptionSelected={(opcao, selecionada) => opcao.id === selecionada.id}
      filterOptions={(lista) => lista}
      noOptionsText={campo.semOpcoes || 'Nenhum resultado'}
      onOpen={() => {
        if (opcoes.length === 0) buscar('');
      }}
      onInputChange={(e, texto, motivo) => {
        if (motivo === 'input') buscar(texto);
      }}
      onChange={(e, escolhido) => onChange(escolhido)}
      renderInput={(params) => (
        <TextField {...params} label={campo.rotulo} variant="outlined" />
      )}
    />
  );
};

// Campo de data (AAAA-MM-DD); vazio é ''. `min`/`max` vêm do campo ligado (ver `naoAntesDe`).
const CampoData = ({ campo, valor, onChange, min, max }) => (
  <TextField
    type="date"
    fullWidth
    size="small"
    variant="outlined"
    label={campo.rotulo}
    value={valor}
    onChange={(e) => onChange(e.target.value)}
    InputLabelProps={{ shrink: true }}
    inputProps={{ min: min || undefined, max: max || undefined }}
  />
);

const CampoSelecao = ({ campo, valor, onChange }) => (
  <TextField
    select
    fullWidth
    size="small"
    variant="outlined"
    label={campo.rotulo}
    value={valor}
    onChange={(e) => onChange(e.target.value)}
    InputLabelProps={{ shrink: true }}
    SelectProps={{ displayEmpty: true }}
  >
    <MenuItem value="">Todos</MenuItem>
    {campo.opcoes.map((opcao) => (
      <MenuItem key={opcao.id} value={opcao.id}>
        {opcao[campo.rotuloOpcao || 'nome']}
      </MenuItem>
    ))}
  </TextField>
);

/**
 * Faixa "Filtros ativos" (com chips removíveis e "Limpar tudo") + botão que abre uma gaveta
 * com os campos. Controlado e sem react-admin: quem usa guarda os valores e monta a consulta.
 *
 * campos: [{ chave, rotulo, tipo: 'selecao' | 'busca' | 'data', ... }]
 *   - 'selecao': opcoes: [{ id, nome }] (rotuloOpcao: nome da propriedade exibida, padrão
 *     "nome"). O valor é o id; vazio é ''.
 *   - 'busca': buscar(texto) => Promise<[{ id, nome }]> (rotuloOpcao idem). O valor é o
 *     objeto escolhido; vazio é null.
 *   - 'data': o valor é a data AAAA-MM-DD; vazio é ''. Para um período, `naoAntesDe: chave`
 *     (a data não pode ser anterior à do campo indicado) e `naoDepoisDe: chave` (nem posterior).
 *     Ao escolher uma data fora disso, a do outro campo acompanha.
 * valores: { [chave]: valor }
 * onChange(novosValores): chamado ao aplicar, remover um chip ou limpar.
 */
const FiltrosTabela = ({ campos, valores, onChange, ...rest }) => {
  const classes = useStyles();
  const [aberto, setAberto] = useState(false);
  const [rascunho, setRascunho] = useState(valores);

  const valorVazio = (campo) => (campo.tipo === 'busca' ? null : '');
  const todosVazios = () =>
    campos.reduce(
      (acc, campo) => ({ ...acc, [campo.chave]: valorVazio(campo) }),
      {},
    );

  const rotuloDoValor = (campo, valor) => {
    if (campo.tipo === 'busca') return valor[campo.rotuloOpcao || 'nome'];
    if (campo.tipo === 'data') return valor.split('-').reverse().join('/');
    const opcao = campo.opcoes.find((item) => item.id === valor);
    return opcao ? opcao[campo.rotuloOpcao || 'nome'] : '...';
  };

  const ativos = campos.filter((campo) => !vazio(valores[campo.chave]));

  function aplicar() {
    onChange(rascunho);
    setAberto(false);
  }

  function limpar() {
    const limpos = todosVazios();
    setRascunho(limpos);
    onChange(limpos);
    setAberto(false);
  }

  return (
    <>
      <Box className={classes.faixa} {...rest}>
        <Box className={classes.grupo}>
          <Typography variant="body2" className={classes.rotulo}>
            Filtros ativos:
          </Typography>
          {ativos.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              Nenhum
            </Typography>
          )}
          {ativos.map((campo) => (
            <Chip
              key={campo.chave}
              size="small"
              className={classes.chip}
              label={`${campo.rotulo}: ${rotuloDoValor(
                campo,
                valores[campo.chave],
              )}`}
              onDelete={() =>
                onChange({ ...valores, [campo.chave]: valorVazio(campo) })
              }
            />
          ))}
          {ativos.length > 0 && (
            <Button size="small" onClick={limpar}>
              Limpar tudo
            </Button>
          )}
        </Box>
        <Button
          variant="outlined"
          size="small"
          className={classes.botao}
          startIcon={<FilterListIcon />}
          endIcon={<ArrowDropDownIcon />}
          onClick={() => {
            setRascunho(valores);
            setAberto(true);
          }}
        >
          Filtros avançados
        </Button>
      </Box>
      <Drawer anchor="right" open={aberto} onClose={() => setAberto(false)}>
        <div className={classes.drawer}>
          <Box className={classes.drawerTitulo}>
            <Typography variant="h6">Filtros avançados</Typography>
            <IconButton size="small" onClick={() => setAberto(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          {campos.map((campo) => {
            const Campo =
              {
                busca: CampoBusca,
                data: CampoData,
              }[campo.tipo] || CampoSelecao;
            return (
              <Campo
                key={campo.chave}
                campo={campo}
                valor={rascunho[campo.chave]}
                min={campo.naoAntesDe && rascunho[campo.naoAntesDe]}
                max={campo.naoDepoisDe && rascunho[campo.naoDepoisDe]}
                onChange={(valor) => {
                  const novos = { ...rascunho, [campo.chave]: valor };
                  // Fora do limite do campo ligado: ele acompanha a data escolhida.
                  const posterior = campo.naoDepoisDe;
                  const anterior = campo.naoAntesDe;
                  if (
                    valor &&
                    posterior &&
                    novos[posterior] &&
                    valor > novos[posterior]
                  )
                    novos[posterior] = valor;
                  if (
                    valor &&
                    anterior &&
                    novos[anterior] &&
                    valor < novos[anterior]
                  )
                    novos[anterior] = valor;
                  setRascunho(novos);
                }}
              />
            );
          })}
          <Box className={classes.drawerAcoes}>
            <Button
              variant="outlined"
              fullWidth
              className={classes.botaoLimpar}
              onClick={limpar}
            >
              Limpar
            </Button>
            <Button
              variant="contained"
              fullWidth
              className={classes.botaoAplicar}
              onClick={aplicar}
            >
              Aplicar filtros
            </Button>
          </Box>
        </div>
      </Drawer>
    </>
  );
};

export default FiltrosTabela;
